import { useEffect, useState } from 'react';
import { getScrollContainer, subscribeScroll } from '@components/motion';

/**
 * Floating "back to top" button. The page scrolls inside the custom
 * `.page-scroll-root` container (not the window), so it tracks that container via
 * the shared scroll engine and scrolls it to the top on click. Mounts per-page so
 * the engine rebinds to the active page's scroll container after navigation.
 * Appears once the user is past ~60% of a viewport down.
 */
export default function ScrollToTop() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        return subscribeScroll((state) => {
            setVisible(state.scrollTop > state.viewportHeight * 0.6);
        });
    }, []);

    const handleClick = (): void => {
        const el = getScrollContainer();
        if (!el) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        el.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    };

    return (
        <button
            type="button"
            aria-label="Scroll to top"
            onClick={handleClick}
            className={`fixed bottom-6 right-6 z-[1500] flex size-[52px] items-center justify-center rounded-full bg-[#f60] text-white shadow-[0_10px_30px_rgba(255,102,0,0.35)] transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 hover:bg-[#e65c00] lg:bottom-8 lg:right-8 lg:size-[56px] ${
                visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
            }`}
        >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M12 19V5M5 12l7-7 7 7"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
}
